import React, {useState} from "react";
import {
    Box, Stack, Typography, Button, Collapse,
    TextField, Chip, IconButton
} from "@mui/material";
import {IconChevronRight, IconPencil, IconCheck, IconX} from "@tabler/icons-react";
import {DescriptionPanel, SectionHeader} from "./mcp-detail-shared";

const PromptCard = ({name, prompt, onSave}) => {
    const [expanded, setExpanded] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const originalText = typeof prompt === "string"
        ? prompt
        : (prompt?.description ?? JSON.stringify(prompt, null, 2));

    const [editName, setEditName] = useState(name);
    const [editText, setEditText] = useState(originalText);

    const handleEditSave = () => {
        onSave?.(name, {name: editName, text: editText});
        setEditOpen(false);
    };

    const handleEditCancel = () => {
        setEditName(name);
        setEditText(originalText);
        setEditOpen(false);
    };

    return (
        <Box sx={{border: 1, borderColor: "divider", borderRadius: 2, p: 2}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                    direction="row" alignItems="center" gap={1}
                    onClick={() => { if (!editOpen) setExpanded(v => !v); }}
                    sx={{cursor: editOpen ? "default" : "pointer", flex: 1}}
                >
                    <Chip label={name} size="small" sx={{fontSize: 11, fontWeight: 600}}/>
                    {!editOpen && (
                        <IconChevronRight size={14} style={{
                            transition: "transform 0.2s",
                            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                            opacity: 0.4,
                        }}/>
                    )}
                </Stack>
                <IconButton
                    size="small"
                    onClick={() => { setEditOpen(v => !v); setExpanded(false); }}
                    sx={{p: 0.5, color: editOpen ? "primary.main" : "text.secondary"}}
                >
                    <IconPencil size={14}/>
                </IconButton>
            </Stack>

            <Collapse in={expanded && !editOpen}>
                {originalText ? <DescriptionPanel description={originalText}/> : null}
            </Collapse>

            <Collapse in={editOpen}>
                <Box sx={{mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5}}>
                    <Typography fontSize={11} fontWeight={700} textTransform="uppercase" letterSpacing={0.5} color="text.secondary">
                        Edit Prompt
                    </Typography>
                    <Box>
                        <Typography fontSize={11} color="text.secondary" mb={0.5}>Name</Typography>
                        <TextField
                            size="small" fullWidth
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            InputProps={{sx: {fontFamily: "monospace", fontSize: 12, borderRadius: 1}}}
                        />
                    </Box>
                    <Box>
                        <Typography fontSize={11} color="text.secondary" mb={0.5}>Content</Typography>
                        <TextField
                            size="small" fullWidth multiline minRows={3}
                            value={editText}
                            onChange={e => setEditText(e.target.value)}
                            InputProps={{sx: {fontFamily: "monospace", fontSize: 12, borderRadius: 1}}}
                        />
                    </Box>
                    <Stack direction="row" gap={1}>
                        <Button
                            size="small" variant="modal" color="primary"
                            startIcon={<IconCheck size={13}/>}
                            onClick={handleEditSave}
                            sx={{fontSize: 11, textTransform: "none", borderRadius: 1}}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            startIcon={<IconX size={13}/>}
                            onClick={handleEditCancel}
                            sx={{fontSize: 11, textTransform: "none", color: "text.secondary"}}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    );
};

export const MCPPromptsSection = ({prompts, onPromptSave}) => {
    const [open, setOpen] = useState(true);
    const entries = Object.entries(prompts);

    return (
        <Box sx={{py: 2}}>
            <SectionHeader label="Prompts" count={entries.length} open={open} onToggle={() => setOpen(v => !v)}/>
            <Collapse in={open}>
                <Stack gap={1.5}>
                    {entries.length === 0 ? (
                        <Typography fontSize={13} color="text.secondary">No prompts defined</Typography>
                    ) : entries.map(([name, prompt]) => (
                        <PromptCard key={name} name={name} prompt={prompt} onSave={onPromptSave}/>
                    ))}
                </Stack>
            </Collapse>
        </Box>
    );
};

export default MCPPromptsSection;